'use client';

import React, { useCallback } from 'react';
import { Spinner } from '@heroui/react';
import { Typography, UniversalTable } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';
import { EmptyContentIc } from '../../../../components/icons';
import { AccountsTableProps, RevshareAccount } from '../types';
import { getTableColumns } from '../constants';
import IdCell from './IdCell';
import SummaryCell from './SummaryCell';
import IndicatorsCell from './IndicatorsCell';
import StatusCell from './StatusCell';
import ActionsCell from './ActionsCell';

const AccountsTable: React.FC<AccountsTableProps> = ({
  accounts,
  isLoading,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onShowContent,
}) => {
  const { t, lang } = useTranslations();

  const renderCell = useCallback(
    (account: RevshareAccount, columnKey: string) => {
      switch (columnKey) {
        case 'id':
          return <IdCell account={account} />;
        case 'summary':
          return <SummaryCell account={account} />;
        case 'indicators':
          return <IndicatorsCell account={account} />;
        case 'status':
          return <StatusCell account={account} />;
        case 'actions':
          return <ActionsCell account={account} onShowContent={onShowContent} />;
        default:
          return null;
      }
    },
    [onShowContent]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner
          size="lg"
          classNames={{
            circle1: '!border-b-partner',
            circle2: '!border-b-partner/50',
          }}
        />
      </div>
    );
  }

  return (
    <UniversalTable
      key={lang}
      data={accounts}
      columns={getTableColumns(t)}
      renderCell={renderCell}
      emptyContent={
        <div className="text-center py-12">
          <div className="flex flex-col items-center gap-4">
            <EmptyContentIc />
            <div className="flex flex-col gap-1.5">
              <Typography variant="h6" className="text-xl font-semibold leading-7">
                {t('partner.partnerAccounts.noAccounts')}
              </Typography>
              <Typography
                className="text-neutral-500 text-base font-medium leading-6"
                variant="body1"
              >
                {t('partner.partnerAccounts.noAccountsDescription')}
              </Typography>
            </div>
          </div>
        </div>
      }
      pagination={{
        count: totalCount,
        page_size: pageSize,
        page: currentPage,
      }}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      currentPage={currentPage}
      currentPageSize={pageSize}
    />
  );
};

export default AccountsTable;
